import React, { useState, useEffect } from 'react';
import './MyAccount.css';

const ACCOUNT_API = 'https://563t3yrmw5d42zyd7gw6eeeun40htlid.lambda-url.us-east-1.on.aws';
const SELLER_API = 'https://dmk7gtgi23d5kg4xkwoln7cedu0telqy.lambda-url.us-east-1.on.aws';

function MyAccount({ user, onShowDisclaimer }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [bets, setBets] = useState([]);
  const [stats, setStats] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newBet, setNewBet] = useState({ matchup: '', selection: '', odds: '', stake: '' });
  const [feedback, setFeedback] = useState({ type: 'general', message: '' });
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [settlingBet, setSettlingBet] = useState(null);
  const [sellerStatus, setSellerStatus] = useState(null);
  const [sellerForm, setSellerForm] = useState({ display_name: '', bio: '' });
  const [pickForm, setPickForm] = useState({ matchup: '', selection: '', odds: '', game_time: '', reasoning: '' });
  const [sellerSaving, setSellerSaving] = useState(false);
  const [sellerPicks, setSellerPicks] = useState([]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('idToken');
    return token ? { Authorization: `Bearer ${token}` } : { Authorization: user?.sub || '' };
  };

  useEffect(() => {
    loadProfile();
    loadBets();
    loadStats();
    loadSellerStatus();
  }, [user]);

  const handleAuthError = (res) => {
    if (res.status === 401) {
      alert('Session expired. Please log in again.');
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.reload();
      return true;
    }
    return false;
  };

  const loadProfile = async () => {
    try {
      const res = await fetch(`${ACCOUNT_API}/api/account`, { headers: getAuthHeader() });
      if (handleAuthError(res)) return;
      const data = await res.json();
      if (data.needs_setup) {
        // Create profile
        const createRes = await fetch(`${ACCOUNT_API}/api/account`, {
          method: 'POST',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email: user?.email, 
            display_name: user?.name || user?.email?.split('@')[0],
            referral_code: localStorage.getItem('referralCode') // Pass referral code if exists
          })
        });
        if (createRes.ok) {
          localStorage.removeItem('referralCode'); // Clear after use
          loadProfile(); // Reload to get the created profile
        } else {
          console.error('Profile creation failed:', await createRes.json());
        }
      } else {
        setProfile(data);
        // Load referral info after profile is confirmed
        loadReferralInfo();
        if (!data.disclaimer_acknowledged && onShowDisclaimer) {
          onShowDisclaimer();
        }
      }
    } catch (e) {
      console.error('Profile load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadBets = async () => {
    try {
      const res = await fetch(`${ACCOUNT_API}/api/account/bets`, { headers: getAuthHeader() });
      const data = await res.json();
      setBets(data.bets || []);
    } catch (e) {
      console.error('Bets load error:', e);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${ACCOUNT_API}/api/account/stats`, { headers: getAuthHeader() });
      setStats(await res.json());
    } catch (e) {
      console.error('Stats load error:', e);
    }
  };

  const loadReferralInfo = async () => {
    try {
      const res = await fetch(`${ACCOUNT_API}/api/account/referral`, { headers: getAuthHeader() });
      const data = await res.json();
      if (res.ok) {
        setReferralInfo(data);
      }
    } catch (e) {
      console.error('Referral load error:', e);
    }
  };

  const loadSellerStatus = async () => {
    try {
      const res = await fetch(`${SELLER_API}/api/seller/status`, { headers: getAuthHeader() });
      const data = await res.json();
      setSellerStatus(data);
      // If seller, load their picks
      if (data.is_seller && data.seller_id) {
        loadSellerPicks(data.seller_id);
      }
    } catch (e) {
      console.error('Seller status error:', e);
    }
  };

  const loadSellerPicks = async (sellerId) => {
    try {
      const res = await fetch(`${SELLER_API}/api/sellers/${sellerId}/picks`, { headers: getAuthHeader() });
      const data = await res.json();
      setSellerPicks(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Seller picks error:', e);
    }
  };

  const addBet = async (e) => {
    e.preventDefault();
    if (!newBet.matchup || !newBet.selection || !newBet.odds) return;
    
    try {
      await fetch(`${ACCOUNT_API}/api/account/bets`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newBet, odds: parseInt(newBet.odds), source: 'manual' })
      });
      setNewBet({ matchup: '', selection: '', odds: '', stake: '' });
      loadBets();
      loadStats();
    } catch (e) {
      console.error('Add bet error:', e);
    }
  };

  const deleteBet = async (betId) => {
    try {
      await fetch(`${ACCOUNT_API}/api/account/bets/${betId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      loadBets();
      loadStats();
    } catch (e) {
      console.error('Delete bet error:', e);
    }
  };

  const settleBet = async (result) => {
    if (!settlingBet) return;
    try {
      await fetch(`${ACCOUNT_API}/api/account/bets/${settlingBet.bet_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ result })
      });
      setSettlingBet(null);
      loadBets();
      loadStats();
    } catch (e) {
      console.error('Settle bet error:', e);
    }
  };

  const enableSeller = async (e) => {
    e.preventDefault();
    if (!sellerForm.display_name.trim()) return alert('Display name required');
    setSellerSaving(true);
    try {
      const res = await fetch(`${SELLER_API}/api/seller/apply`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(sellerForm)
      });
      const data = await res.json();
      if (data.error) {
        alert(data.error);
      } else {
        alert('✅ Application submitted! You\'ll be notified when approved.');
        loadSellerStatus();
      }
    } catch (e) {
      console.error('Apply seller error:', e);
      alert('Error submitting application');
    } finally {
      setSellerSaving(false);
    }
  };

  const postPick = async (e) => {
    e.preventDefault();
    if (!pickForm.matchup || !pickForm.selection || !pickForm.odds || !pickForm.game_time) {
      return alert('Matchup, selection, odds, and game time required');
    }
    setSellerSaving(true);
    try {
      // Convert datetime-local to ISO format
      const gameTimeISO = new Date(pickForm.game_time).toISOString();
      const res = await fetch(`${SELLER_API}/api/seller/picks`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pickForm, odds: parseInt(pickForm.odds), game_time: gameTimeISO })
      });
      const data = await res.json();
      if (data.error) {
        alert(data.error);
      } else {
        alert('✅ Pick posted!');
        setPickForm({ matchup: '', selection: '', odds: '', game_time: '', reasoning: '' });
      }
    } catch (e) {
      alert('Error posting pick');
    } finally {
      setSellerSaving(false);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedback.message.trim()) return;
    
    try {
      await fetch(`${ACCOUNT_API}/api/account/feedback`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...feedback, email: user?.email })
      });
      setFeedback({ type: 'general', message: '' });
      setFeedbackSent(true);
      setTimeout(() => setFeedbackSent(false), 3000);
    } catch (e) {
      console.error('Feedback error:', e);
    }
  };

  const copyReferralLink = () => {
    if (referralInfo?.referral_link) {
      navigator.clipboard.writeText(referralInfo.referral_link);
      alert('Referral link copied!');
    }
  };

  const startSubscription = async () => {
    try {
      const res = await fetch(`${ACCOUNT_API}/api/account/create-checkout`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert('Error creating checkout session');
      }
    } catch (e) {
      console.error('Checkout error:', e);
      alert('Error starting subscription');
    }
  };

  if (loading) {
    return <div className="my-account loading">Loading...</div>;
  }

  return (
    <div className="my-account">
      <div className="account-header">
        <h2>👤 My Account</h2>
        <div className="account-tabs">
          {['overview', 'bets', ...(sellerStatus?.is_seller ? ['betslip'] : []), 'seller', 'referrals', 'feedback'].map(tab => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' && '📊 Overview'}
              {tab === 'bets' && '🎯 My Bets'}
              {tab === 'betslip' && '📋 Public Bet Slip'}
              {tab === 'seller' && '💰 Sell Picks'}
              {tab === 'referrals' && '🔗 Referrals'}
              {tab === 'feedback' && '💬 Feedback'}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="tab-content overview">
          <div className="profile-card">
            <h3>Profile</h3>
            <div className="profile-info">
              <p><strong>Email:</strong> {profile?.email}</p>
              <p><strong>Name:</strong> {profile?.display_name}</p>
              <p><strong>Member since:</strong> {profile?.created_at?.split(' ')[0]}</p>
            </div>
          </div>

          <div className="subscription-card">
            <h3>Subscription</h3>
            <div className="sub-status">
              <span className={`status-badge ${profile?.subscription_status}`}>
                {profile?.subscription_status?.toUpperCase()}
              </span>
              {profile?.subscription_status === 'trial' && (
                <p className="trial-info">
                  {profile?.trial_days_remaining} days remaining in free trial
                </p>
              )}
            </div>
            {profile?.subscription_status === 'trial' && (
              <div className="payment-prompt">
                {profile?.trial_days_remaining <= 9 ? (
                  <p>⚠️ Your trial ends soon! Subscribe now to keep access.</p>
                ) : (
                  <p>Subscribe now for $20/month to lock in your access.</p>
                )}
                <button className="add-payment-btn" onClick={startSubscription}>
                  Subscribe - $20/month
                </button>
              </div>
            )}
            {profile?.subscription_status === 'active' && (
              <div className="active-sub">
                <p>✅ Your subscription is active. Thank you for your support!</p>
              </div>
            )}
          </div>

          <div className="stats-card">
            <h3>My Performance</h3>
            {stats && (
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-value">{stats.total_bets}</span>
                  <span className="stat-label">Total Bets</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.wins}-{stats.losses}</span>
                  <span className="stat-label">Record</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.win_rate}%</span>
                  <span className="stat-label">Win Rate</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.roi > 0 ? '+' : ''}{stats.roi}%</span>
                  <span className="stat-label">ROI</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'seller' && (
        <div className="tab-content seller">
          <div className="seller-card">
            <h3>🏆 {sellerStatus?.is_seller ? 'Post Picks' : 'Become a Seller'}</h3>
            {sellerStatus?.is_seller ? (
              <div className="seller-active">
                <p>✅ You're a verified seller: <strong>{sellerStatus.display_name}</strong></p>
                <form onSubmit={postPick} className="seller-form post-pick-form">
                  <h4>Post a Pick</h4>
                  <input
                    type="text"
                    placeholder="Matchup (e.g., Lakers @ Celtics)"
                    value={pickForm.matchup}
                    onChange={e => setPickForm({ ...pickForm, matchup: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Selection (e.g., Lakers -3.5)"
                    value={pickForm.selection}
                    onChange={e => setPickForm({ ...pickForm, selection: e.target.value })}
                    required
                  />
                  <div className="form-row">
                    <input
                      type="number"
                      placeholder="Odds (e.g., -110)"
                      value={pickForm.odds}
                      onChange={e => setPickForm({ ...pickForm, odds: e.target.value })}
                      required
                    />
                    <input
                      type="datetime-local"
                      value={pickForm.game_time}
                      onChange={e => setPickForm({ ...pickForm, game_time: e.target.value })}
                      required
                    />
                  </div>
                  <textarea
                    placeholder="Reasoning (optional)"
                    value={pickForm.reasoning}
                    onChange={e => setPickForm({ ...pickForm, reasoning: e.target.value })}
                    rows={2}
                  />
                  <button type="submit" disabled={sellerSaving}>
                    {sellerSaving ? 'Posting...' : 'Post Pick'}
                  </button>
                </form>
                <p className="seller-note">⚠️ Picks are immutable and auto-verified by final scores.</p>
              </div>
            ) : sellerStatus?.application_status === 'pending' ? (
              <div className="seller-pending">
                <p>⏳ Your application is pending review.</p>
                <p className="seller-note">You'll be notified when approved.</p>
              </div>
            ) : sellerStatus?.application_status === 'rejected' ? (
              <div className="seller-rejected">
                <p>❌ Your application was not approved.</p>
                <p className="seller-note">Contact support for more info.</p>
              </div>
            ) : (
              <div className="seller-setup">
                <p>Share your picks with a verified, immutable track record.</p>
                <form onSubmit={enableSeller} className="seller-form">
                  <input
                    type="text"
                    placeholder="Display Name (public)"
                    value={sellerForm.display_name}
                    onChange={e => setSellerForm({ ...sellerForm, display_name: e.target.value })}
                    maxLength={100}
                    required
                  />
                  <textarea
                    placeholder="Bio (optional) - Tell people about your betting style"
                    value={sellerForm.bio}
                    onChange={e => setSellerForm({ ...sellerForm, bio: e.target.value })}
                    maxLength={500}
                    rows={3}
                  />
                  <button type="submit" disabled={sellerSaving}>
                    {sellerSaving ? 'Submitting...' : 'Apply to Sell Picks'}
                  </button>
                </form>
                <p className="seller-note">Admin approval required. Your picks will be auto-verified.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'bets' && (
        <div className="tab-content bets">
          <div className="add-bet-section">
            <h3>📝 Add Pick</h3>
            <form onSubmit={addBet} className="add-bet-form">
              <input
                type="text"
                placeholder="Matchup (e.g., Lakers @ Celtics)"
                value={newBet.matchup}
                onChange={e => setNewBet({ ...newBet, matchup: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Selection (e.g., Lakers +3.5)"
                value={newBet.selection}
                onChange={e => setNewBet({ ...newBet, selection: e.target.value })}
                required
              />
              <input
                type="number"
                placeholder="Odds (e.g., -110)"
                value={newBet.odds}
                onChange={e => setNewBet({ ...newBet, odds: e.target.value })}
                required
              />
              <input
                type="number"
                placeholder="Stake $ (optional)"
                value={newBet.stake}
                onChange={e => setNewBet({ ...newBet, stake: e.target.value })}
              />
              <button type="submit">Add Pick</button>
            </form>
          </div>

          <div className="bets-list">
            <h3>📋 My Bet Slip ({bets.length})</h3>
            {bets.length === 0 ? (
              <p className="no-bets">No bets tracked yet. Add picks above or from AI Cards.</p>
            ) : (
              <div className="bets-table">
                {bets.map(bet => (
                  <div key={bet.bet_id} className={`bet-row ${bet.result}`}>
                    <div className="bet-info">
                      <span className="matchup">{bet.matchup}</span>
                      <span className="selection">{bet.selection}</span>
                      <span className="odds">{bet.odds > 0 ? '+' : ''}{bet.odds}</span>
                    </div>
                    <div className="bet-meta">
                      {bet.result === 'pending' ? (
                        <button 
                          className="settle-btn"
                          onClick={() => setSettlingBet(bet)}
                        >
                          ⚖️ Settle
                        </button>
                      ) : (
                        <span className={`result-badge ${bet.result}`}>
                          {bet.result.toUpperCase()}
                        </span>
                      )}
                      <span className="source">{bet.source}</span>
                      <button 
                        className="delete-btn"
                        onClick={() => deleteBet(bet.bet_id)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Settle Modal */}
          {settlingBet && (
            <div className="modal-overlay" onClick={() => setSettlingBet(null)}>
              <div className="settle-modal" onClick={e => e.stopPropagation()}>
                <h3>⚖️ Settle Pick</h3>
                <div className="settle-bet-info">
                  <p><strong>{settlingBet.matchup}</strong></p>
                  <p>{settlingBet.selection} ({settlingBet.odds > 0 ? '+' : ''}{settlingBet.odds})</p>
                </div>
                <div className="settle-buttons">
                  <button className="settle-won" onClick={() => settleBet('won')}>✅ Won</button>
                  <button className="settle-lost" onClick={() => settleBet('lost')}>❌ Lost</button>
                  <button className="settle-push" onClick={() => settleBet('push')}>➖ Push</button>
                </div>
                <button className="settle-cancel" onClick={() => setSettlingBet(null)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'betslip' && sellerStatus?.is_seller && (
        <div className="tab-content betslip">
          <div className="betslip-header">
            <h3>📋 Your Public Bet Slip</h3>
            <p>These picks are visible to your subscribers on the Verified Picks page</p>
          </div>
          
          <div className="betslip-stats">
            {(() => {
              const settled = sellerPicks.filter(p => p.result !== 'pending');
              const wins = settled.filter(p => p.result === 'won').length;
              const losses = settled.filter(p => p.result === 'lost').length;
              const winRate = settled.length > 0 ? (wins / settled.length * 100).toFixed(1) : 0;
              const roi = settled.length > 0 ? ((wins * 0.91 - losses) / settled.length * 100).toFixed(1) : 0;
              return (
                <>
                  <div className="stat-box"><span>{sellerPicks.length}</span>Total Picks</div>
                  <div className="stat-box"><span>{wins}-{losses}</span>Record</div>
                  <div className="stat-box"><span>{winRate}%</span>Win Rate</div>
                  <div className="stat-box"><span>{roi > 0 ? '+' : ''}{roi}%</span>ROI</div>
                </>
              );
            })()}
          </div>

          <div className="betslip-picks">
            {sellerPicks.length === 0 ? (
              <p className="no-picks">No picks posted yet. Go to "Sell Picks" tab to post your first pick!</p>
            ) : (
              sellerPicks.map(pick => (
                <div key={pick.pick_id} className={`betslip-pick ${pick.result}`}>
                  <div className="pick-header">
                    <span className="matchup">{pick.matchup}</span>
                    <span className={`result-badge ${pick.result}`}>
                      {pick.result === 'pending' ? '⏳ Pending' : pick.result === 'won' ? '✅ Won' : '❌ Lost'}
                    </span>
                  </div>
                  <div className="pick-details">
                    <span className="selection">{pick.selection}</span>
                    <span className="odds">({pick.odds > 0 ? '+' : ''}{pick.odds})</span>
                  </div>
                  <div className="pick-meta">
                    <span>🕐 {new Date(pick.game_time).toLocaleString()}</span>
                    {pick.reasoning && <span className="reasoning">💭 {pick.reasoning}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'referrals' && (
        <div className="tab-content referrals">
          <div className="referral-card">
            <h3>🎁 Refer Friends, Earn Credits</h3>
            <p>Get $5/month credit for each friend who subscribes (up to 12 months per referral)</p>
            
            <div className="referral-link-box">
              <input 
                type="text" 
                value={referralInfo?.referral_link || ''} 
                readOnly 
              />
              <button onClick={copyReferralLink}>📋 Copy</button>
            </div>

            <div className="referral-stats">
              <div className="ref-stat">
                <span className="ref-value">{referralInfo?.total_referrals || 0}</span>
                <span className="ref-label">Total Referrals</span>
              </div>
              <div className="ref-stat">
                <span className="ref-value">{referralInfo?.active_referrals || 0}</span>
                <span className="ref-label">Active (Paying)</span>
              </div>
              <div className="ref-stat highlight">
                <span className="ref-value">${referralInfo?.monthly_credit || 0}</span>
                <span className="ref-label">Monthly Credit</span>
              </div>
            </div>

            <div className="referral-info">
              <h4>How it works:</h4>
              <ol>
                <li>Share your unique link with friends</li>
                <li>They sign up and start their 30-day free trial</li>
                <li>When they subscribe, you earn $5/month credit</li>
                <li>Credits apply automatically to your subscription</li>
                <li>Refer 4 friends = FREE subscription!</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'feedback' && (
        <div className="tab-content feedback">
          <div className="feedback-card">
            <h3>💬 Send Feedback</h3>
            <p>Help us improve! Report bugs, request features, or share your thoughts.</p>
            
            {feedbackSent ? (
              <div className="feedback-success">
                ✅ Thank you for your feedback!
              </div>
            ) : (
              <form onSubmit={submitFeedback} className="feedback-form">
                <select
                  value={feedback.type}
                  onChange={e => setFeedback({ ...feedback, type: e.target.value })}
                >
                  <option value="general">General Feedback</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature">Feature Request</option>
                </select>
                <textarea
                  placeholder="Your message..."
                  value={feedback.message}
                  onChange={e => setFeedback({ ...feedback, message: e.target.value })}
                  rows={5}
                  required
                />
                <button type="submit">Send Feedback</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MyAccount;
